import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";

import { GetAccessHistory } from "../../services/consent";
import { useSessionContext } from "../../contexts/SessionContext";
import { BundlePatientSummary } from "../../services/fhir";
import { useDict } from "../../utils/useDict";

/** Quantidade exibida no preview antes de "Ver tudo". */
export const ACCESS_HISTORY_PREVIEW_LIMIT = 5;

export type AccessHistoryRow = {
  identifier: string;
  name: string;
  purposeOfUse: string;
  docType: string;
  binaryUrl: string;
  date: string;
  hour: string;
  organization: string;
};

function displayPurposeOfUse(
  purpose: string | undefined,
  instituicao: boolean | undefined,
  identifier: string | undefined,
  username: string
): string {
  switch (purpose) {
    case "PATRQT":
      return "Consentimento individual";
    case "PWATRNY":
      return "Responsável legal";
    case "Emergencia":
      return "Emergência";
    case "Atendimento":
      return instituicao
        ? "Atendimento"
        : identifier === username
          ? "Acesso pessoal"
          : "Consentimento individual";
    case "Federacao":
      return "Atendimento na rede de saúde";
    default:
      return purpose || "-";
  }
}

type UseAccessHistoryDataOptions = {
  /** Limita registros na API (preview). Omitir para lista completa. */
  count?: number;
  enabled?: boolean;
};

export function useAccessHistoryData(options?: UseAccessHistoryDataOptions) {
  const { user } = useSessionContext();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AccessHistoryRow[]>([]);
  const enabled = options?.enabled ?? true;
  const count = options?.count;

  const fetchData = useCallback(async () => {
    if (!user?.username) return;

    setLoading(true);
    try {
      const documentReference = await BundlePatientSummary({
        identifier: user.username,
        entries: ["DocumentReference"],
      });

      const documentResource = documentReference.entry
        ?.flatMap((el: any) => el.resource.entry)
        ?.filter(
          (item: any) =>
            item !== undefined &&
            item.resource.resourceType.includes("DocumentReference")
        );

      const response = await GetAccessHistory(user.username, { count });
      if (response?.data) {
        const rows = response?.data?.map((item: any) => {
          const auxDoc = documentResource?.find(
            (doc: any) => doc?.resource?.id === item?.sujeito?.id
          );
          return {
            identifier: item.sujeito.id || "-",
            name: item.sujeito.nome || "-",
            purposeOfUse: displayPurposeOfUse(
              item.contexto.motivoAcesso,
              item.contexto.instituicao,
              item.sujeito.id,
              user.username
            ),
            docType:
              useDict.signedDocsType[
                auxDoc?.resource?.category?.[0]?.coding?.[0]?.display
              ] || "",
            binaryUrl: auxDoc?.resource?.content?.[0]?.attachment?.url || "",
            date: item.hora && dayjs(item.hora)?.format("DD/MM/YYYY"),
            hour: item.hora && dayjs(item.hora)?.format("HH:mm"),
            organization: item.contexto.instituicao || "-",
          };
        });
        setData(rows ?? []);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("useAccessHistoryData", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [count, user?.username]);

  useEffect(() => {
    if (enabled) {
      void fetchData();
    }
  }, [enabled, fetchData]);

  return { data, loading, refetch: fetchData };
}
